import type { Preferences } from '@beyond-js/ui';
import { Admission } from '@cdn/admission';
import { Api, Credentials } from '@cdn/api';
import type { Settings } from '@cdn/config';
import { Docs } from '@cdn/docs';
import { Family } from '@cdn/family';
import { I18n, Locale } from '@cdn/i18n';
import { AdministrationIdentity, Departures, Destination, Drafts, PlatformIdentity, Renewal, Session, SupabaseIdentity, Vault, type Identity, type Pace } from '@cdn/identity';
import { Paths } from '@cdn/navigation';
import { BroadcastTransport, PollingTransport, Realtime } from '@cdn/realtime';
import { Theme } from '@cdn/shell';
import { Device } from './device';

interface Environment {
	/** Origin and query string of the entry address */
	location: { origin: string; search: string };
	local?: Storage;
	tab?: Storage;
	languages?: readonly string[];
	dark?: boolean;
	root?: HTMLElement;
	fetch?: typeof fetch;
	/** Sends the browser to another address; tests observe it instead of leaving */
	navigate?: (url: string) => void;
	/** The address in view, absolute (the browser's own by default) */
	here?: () => string;
	/** How the page's `Session` renews a session that ended: tests shorten its bounds or turn the silent attempt off */
	pace?: Pace;
	/** Milliseconds after which a session check the service did not answer is tried again (D132); tests shorten them */
	again?: readonly number[];
}

/**
 * The composition root: creates the long-lived objects of the application from the settings and wires them
 * together. It is the only place that knows which identity and realtime adapters were configured, and the
 * place where a scope change is turned into a reset of the realtime subscriptions.
 */
export class Composition {
	readonly api: Api;
	readonly i18n: I18n;
	readonly theme: Theme;
	/** The person's language and appearance: the device copy, replaced by the account's values on arrival */
	readonly preferences: Preferences;
	readonly docs: Docs;
	/** Origin of published delivery, empty when not configured */
	readonly delivery: string;
	readonly session: Session;
	/** The family's answer to a session that ends while the person works: one `Session` of `@beyond-js/ui` */
	readonly renewal: Renewal;
	readonly admission: Admission;
	readonly realtime: Realtime;
	/** The family bar's descriptor for the place in view, relayed by the service from Beyond Projects */
	readonly family: Family;

	#releases: Array<() => void> = [];

	constructor(settings: Settings, environment: Environment) {
		const { location, local, tab } = environment;

		this.api = new Api({ origin: settings.api.origin, credentials: new Credentials(), fetch: environment.fetch, timeout: settings.api.timeout });
		this.preferences = Device.open({ storage: local, locale: Locale.initial({ search: location.search, preferred: environment.languages, tab }), root: environment.root });
		this.i18n = new I18n({ locale: new Locale({ preferences: this.preferences }), loud: settings.mode === 'development' });
		this.theme = new Theme({ preferences: this.preferences, dark: environment.dark });
		this.docs = new Docs(settings.links);
		this.delivery = settings.links.delivery;

		const returning = `${location.origin}${Paths.patterns.returning}`;
		const identity: Identity =
			settings.identity.adapter === 'platform'
				? new PlatformIdentity({ service: settings.identity.service, returning, fetch: environment.fetch, navigate: environment.navigate, timeout: settings.api.timeout })
				: settings.identity.adapter === 'supabase'
					? new SupabaseIdentity({ ...settings.identity.supabase, returning, storage: tab })
					: new AdministrationIdentity({ authority: settings.identity.authority, vault: new Vault(tab), fetch: environment.fetch, timeout: settings.api.timeout });

		this.session = new Session({
			api: this.api,
			identity,
			destination: new Destination({ own: location.origin, origins: settings.identity.returns, storage: tab }),
			drafts: new Drafts(tab),
			departures: new Departures(tab),
			storage: local,
			navigate: environment.navigate,
			again: environment.again
		});

		const here = environment.here ?? (() => globalThis.location.href);
		this.renewal = new Renewal({
			session: this.session,
			api: this.api,
			identity,
			here,
			accounts: this.docs.account,
			leave: returning => this.docs.leave(returning),
			language: () => this.i18n.locale.code,
			navigate: environment.navigate ?? (url => globalThis.location.assign(url)),
			pace: environment.pace,
			// Live transports reconnect once the session was renewed; `realtime` exists by the time it is
			onrenewed: () => this.realtime.resume()
		});

		// The person's own admission to management and preparation, read after every sign-in
		this.admission = new Admission({ api: this.api, session: this.session });

		this.family = new Family(this.api);

		this.realtime = new Realtime({
			api: this.api,
			adapter: settings.realtime.adapter,
			transports: { broadcast: new BroadcastTransport(), polling: new PollingTransport({ api: this.api, interval: settings.realtime.polling }) },
			// A stream that drops says nothing of its own while the page's session is not signed (D63)
			check: () => this.renewal.check()
		});

		// Another organization, another account or no account: nothing subscribed under the previous scope survives
		this.#releases.push(this.session.scope.on(() => this.realtime.reset()));
		// The account's language and appearance win when the application learns who the person is (decision D07)
		this.#releases.push(this.session.arrival.on(values => void this.preferences.apply(values)));
	}

	destroy() {
		for (const release of this.#releases) release();
		this.realtime.reset();
		this.admission.destroy();
		this.renewal.destroy();
		this.session.destroy();
		this.i18n.destroy();
		this.theme.destroy();
	}
}
